import { FC, useState, useEffect, useCallback } from 'react';
import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Settings48Regular, BinRecycle24Regular, PersonDelete24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../Contexts/NotificationContext';
import { useConfirm } from '../../Contexts/hooks';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { acquireAccessToken } from '../../Security/azureAdAuthentication';
import { appConfig } from '../../AppConfig';
import { useConversations, useFeature, useUserPreferences } from '../../store/hooks';
import { clearConversationsAction } from '../../store/actions/conversationActions';
import { setUserPreferencesAction } from '../../store/actions/userPreferencesAction';
import { IUserPreferences, UserPreferences } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';

import ConversationSettingsView from './ConversationSettingsView';

// Constants for voice entry types
const VOICE_ENTRY_TYPE_CLICK_SEND = 'Click Send';
const VOICE_ENTRY_TYPE_AUTOMATIC_SEND = 'Automatic Send';

const useStyles = makeStyles({
    container: {
        background: 'var(--border-bg)',
        color: 'var(--neutral-foreground1)',
    },
    card: {
        background: 'var(--colorCardBackground1)',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        marginTop: '24px',
        marginBottom: '24px',
        padding: '24px',
        paddingRight: '80px',
        border: '1px solid var(--neutral-stroke1)',
        '@media (min-width: 1024px)': {
            width: '80%',
            maxWidth: '920px',
            marginLeft: 'auto',
            marginRight: 'auto',
        },
    },
    titleFlexRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
    },
    flexRowMargin: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '8px',
    },
    sectionTitle: {
        color: 'var(--neutral-foreground1) !important',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        letterSpacing: '0.09px',
        marginBottom: '8px',
        marginLeft: '6px',
    },
    sectionSubTitle: {
        color: 'var(--section-description-color)',
        fontSize: '16px',
        fontWeight: 600,
        fontStyle: 'normal',
        lineHeight: '24px',
        letterSpacing: '0.08px',
    },
    sectionDescription: {
        color: 'var(--neutral-foreground2)',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        letterSpacing: '0.07px',
    },
    errorText: {
        color: 'var(--palette-red-foreground3)',
    },
    iconPrimary: {
        marginRight: '4px',
        marginTop: '-4px',
        color: 'var(--color-primary-500)',
    },
    dropdownHeader: {
        marginTop: '10px',
        marginBottom: '16px',
        maxWidth: '240px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid var(--neutral-stroke1)',
        background: 'var(--neutral-background1)',
        padding: '4px 6px',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '24px',
        color: 'var(--neutral-foreground1)',
        outline: 'none',
        transition: 'box-shadow 0.2s',
    },
    dropdownHeaderButton: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '24px',
    },
    dropdownOptions: {
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '24px',
    },
    inputAutoSend: {
        width: '80px',
        marginTop: '8px',
    },
    visibleLabel: {
        fontWeight: 600,
        color: 'var(--neutral-foreground2)',
        fontSize: '16px',
    },
    marginTopAutomaticSend: {
        marginTop: '-4px',
        marginBottom: '16px',
    },
    marginBottom2: {
        marginBottom: '2px',
    },
    marginBottom1: {
        marginBottom: '1px',
    },
    marginClearAllConversationsDescription: {
        display: 'block',
        marginTop: '0px',
        marginBottom: '16px !important',
    },
});

const ConversationsSettingsHandler: FC = () => {
    // Feature flags
    const DisenrollFromPlatformAvailable = false;

    const classes = useStyles();
    const { t } = useTranslation();
    const confirm = useConfirm();
    const { dispatchNotification } = useNotification();
    const { dispatch } = useConversations();
    // Get chat feature for supported languages
    const chatFeature = useFeature('chat');
    const { userPreferences, dispatch: userPreferencesDispatch } = useUserPreferences();

    // Get supported languages from chat feature
    let speechToTextSupportedLanguages = chatFeature.speechToText?.languages || [];
    // Add Auto-detect as the first option if not already present
    if (!speechToTextSupportedLanguages.some((lang) => lang.locale === 'auto')) {
        speechToTextSupportedLanguages = [
            { locale: 'auto', displayLanguage: 'Auto-detection' },
            ...speechToTextSupportedLanguages,
        ];
    }
    const [selectedLanguage, setSelectedLanguage] = useState(
        userPreferences?.speechToTextLanguage || speechToTextSupportedLanguages[0]?.locale || 'en-US',
    );
    const [voiceEntryType, setVoiceEntryType] = useState(
        userPreferences?.speechClickSend !== undefined
            ? userPreferences.speechClickSend
                ? VOICE_ENTRY_TYPE_CLICK_SEND
                : VOICE_ENTRY_TYPE_AUTOMATIC_SEND
            : VOICE_ENTRY_TYPE_CLICK_SEND,
    );
    const [autoSendNumSecs, setAutoSendNumSecs] = useState<number>(userPreferences?.speechAutoSendNumSecs || 3);
    const [autoSendNumSecsInput, setAutoSendNumSecsInput] = useState<string>(
        (userPreferences?.speechAutoSendNumSecs || 3).toString(),
    );
    const [autoSendNumSecsError, setAutoSendNumSecsError] = useState<string>('');
    const [isClearingConversations, setIsClearingConversations] = useState(false);
    const [showLanguageSelectionInPrompt, setShowLanguageSelectionInPrompt] = useState<boolean>(
        userPreferences?.showLanguageSelectionInPrompt ?? true,
    );
    // Using the correct configuration property for survey management
    // const surveyManagementEnabled = chatFeature?.survey?.isManagementEnabled ?? false;

    // Update local state when user preferences change
    useEffect(() => {
        if (userPreferences?.speechToTextLanguage) {
            setSelectedLanguage(userPreferences.speechToTextLanguage);
        }
        if (userPreferences?.speechAutoSendNumSecs != null) {
            setAutoSendNumSecs(userPreferences.speechAutoSendNumSecs);
            setAutoSendNumSecsInput(userPreferences.speechAutoSendNumSecs.toString());
        }
        if (userPreferences?.speechClickSend !== undefined) {
            setVoiceEntryType(userPreferences.speechClickSend ? VOICE_ENTRY_TYPE_CLICK_SEND : VOICE_ENTRY_TYPE_AUTOMATIC_SEND);
        }
        if (userPreferences?.showLanguageSelectionInPrompt !== undefined) {
            setShowLanguageSelectionInPrompt(userPreferences.showLanguageSelectionInPrompt);
        }
    }, [
        userPreferences?.speechToTextLanguage,
        userPreferences?.speechAutoSendNumSecs,
        userPreferences?.speechClickSend,
        userPreferences?.showLanguageSelectionInPrompt,
    ]);
    const voiceEntryTypes = [
        { key: VOICE_ENTRY_TYPE_CLICK_SEND, text: VOICE_ENTRY_TYPE_CLICK_SEND },
        { key: VOICE_ENTRY_TYPE_AUTOMATIC_SEND, text: VOICE_ENTRY_TYPE_AUTOMATIC_SEND },
    ];

    // Function to validate auto-send seconds input
    const validateAutoSendNumSecs = useCallback(
        (value: string) => {
            if (value === '') {
                setAutoSendNumSecsError('');
                return false;
            }

            const numValue = parseInt(value);
            if (isNaN(numValue)) {
                setAutoSendNumSecsError(t('settings.voice.autoSendNumSecsError.invalid', 'Please enter a valid number'));
                return false;
            }

            if (numValue < 1) {
                setAutoSendNumSecsError(t('settings.voice.autoSendNumSecsError.tooLow', 'Minimum value is 1 second'));
                return false;
            }

            if (numValue > 5) {
                setAutoSendNumSecsError(t('settings.voice.autoSendNumSecsError.tooHigh', 'Maximum value is 5 seconds'));
                return false;
            }

            setAutoSendNumSecsError('');
            return true;
        },
        [t],
    );

    // Function to update language preference
    const onUpdateUserPreferencesLanguage = useCallback(
        (locale: string) => {
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    setSelectedLanguage(locale);
                    // Update user preferences with the locale language
                    const updatedUserPreferences: IUserPreferences = {
                        ...userPreferences,
                        speechToTextLanguage: locale,
                    };
                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    userPreferencesDispatch(setUserPreferencesAction(updatedUserPreferences));
                } catch (error) {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                    console.error('Error updating language preference:', error);
                }
            });
        },
        [userPreferences, userPreferencesDispatch, dispatchNotification],
    );
    // Function to update auto-send number of seconds preference
    const onUpdateAutoSendNumSecs = useCallback(
        (numSecs: number) => {
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    setAutoSendNumSecs(numSecs);
                    setAutoSendNumSecsInput(numSecs.toString());
                    // Update user preferences with the auto-send number of seconds
                    const updatedUserPreferences: IUserPreferences = {
                        ...userPreferences,
                        speechAutoSendNumSecs: numSecs,
                    };
                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    userPreferencesDispatch(setUserPreferencesAction(updatedUserPreferences));
                } catch (error) {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                    console.error('Error updating auto-send preference:', error);
                }
            });
        },
        [userPreferences, userPreferencesDispatch, dispatchNotification],
    );

    // Function to update voice entry type preference (speechClickSend)
    const onUpdateVoiceEntryType = useCallback(
        (entryType: string) => {
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    setVoiceEntryType(entryType);
                    const speechClickSend = entryType === VOICE_ENTRY_TYPE_CLICK_SEND;
                    // Update user preferences with the voice entry type
                    const updatedUserPreferences: IUserPreferences = {
                        ...userPreferences,
                        speechClickSend: speechClickSend,
                    };
                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    userPreferencesDispatch(setUserPreferencesAction(updatedUserPreferences));
                } catch (error) {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                    console.error('Error updating voice entry type preference:', error);
                }
            });
        },
        [userPreferences, userPreferencesDispatch, dispatchNotification],
    );

    // Function to update show/hide language selection preference
    const onUpdateShowLanguageSelectionInPrompt = useCallback(
        (show: boolean) => {
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    setShowLanguageSelectionInPrompt(show);
                    const updatedUserPreferences: IUserPreferences = {
                        ...userPreferences,
                        showLanguageSelectionInPrompt: show,
                    };
                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    userPreferencesDispatch(setUserPreferencesAction(updatedUserPreferences));
                } catch (error) {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                    console.error('Error updating show/hide language selection:', error);
                }
            });
        },
        [userPreferences, userPreferencesDispatch, dispatchNotification],
    );

    const handleClearConversations = () => {
        // guard against double submission (e.g. user clicking the button again while the request is in flight)
        if (isClearingConversations) {
            return;
        }
        confirm({
            message: '',
            show: true,
            title: t('menu.persona.confirmClearConversations'),
            cancelText: t('No'),
            confirmText: t('Yes'),
        }).then(() => {
            setIsClearingConversations(true);
            performEnterpriseGPTApiCall(async (client, signal) => {
                try {
                    await client.deleteAllUserConversations(signal);
                    dispatch(clearConversationsAction());
                    dispatchNotification('settings.clearConversations.success', 'success');
                } catch {
                    dispatchNotification('error.api.delete.conversations', 'error');
                } finally {
                    setIsClearingConversations(false);
                }
            });
        });
    };
    const handleDisenroll = () => {
        confirm({
            message: t('menu.persona.disenrollInfo'),
            show: true,
            title: t('menu.persona.disenrollConfirm'),
            cancelText: t('No'),
            confirmText: t('Yes'),
        }).then(() => {
            performEnterpriseGPTApiCall(async (client, cancelToken) => {
                try {
                    await client.unenroll(cancelToken);
                    document.location.reload();
                } catch {
                    dispatchNotification('error.api.delete.enrollment', 'error');
                }
            });
        });
    };

    // Create a wrapper function for the translation function to match expected signature
    const translationWrapper = (key: string, defaultValue?: string) => {
        return t(key, defaultValue || key);
    };

    // Filter and map speechToTextSupportedLanguages to ensure proper typing
    const mappedSpeechToTextLanguages = speechToTextSupportedLanguages
        .filter((lang): lang is Required<Pick<typeof lang, 'locale' | 'displayLanguage'>> =>
            Boolean(lang.locale && lang.displayLanguage),
        )
        .map((lang) => ({
            locale: lang.locale!,
            displayLanguage: lang.displayLanguage!,
        }));

    return (
        <ConversationSettingsView
            classes={classes}
            t={translationWrapper}
            DisenrollFromPlatformAvailable={DisenrollFromPlatformAvailable}
            selectedLanguage={selectedLanguage}
            speechToTextSupportedLanguages={mappedSpeechToTextLanguages}
            voiceEntryType={voiceEntryType}
            voiceEntryTypes={voiceEntryTypes}
            autoSendNumSecsInput={autoSendNumSecsInput}
            autoSendNumSecsError={autoSendNumSecsError}
            showLanguageSelectionInPrompt={showLanguageSelectionInPrompt}
            handleClearConversations={handleClearConversations}
            isClearingConversations={isClearingConversations}
            handleDisenroll={handleDisenroll}
            onUpdateUserPreferencesLanguage={onUpdateUserPreferencesLanguage}
            onUpdateVoiceEntryType={onUpdateVoiceEntryType}
            onUpdateAutoSendNumSecs={onUpdateAutoSendNumSecs}
            onUpdateShowLanguageSelectionInPrompt={onUpdateShowLanguageSelectionInPrompt}
            validateAutoSendNumSecs={validateAutoSendNumSecs}
            setAutoSendNumSecsInput={setAutoSendNumSecsInput}
        />
    );
};

export default ConversationsSettingsHandler;
